import {
  Button,
  Group,
  Modal,
  ScrollArea,
  SegmentedControl,
  Stack,
  Tooltip,
} from "@mantine/core"
import { useMemo, useState } from "react"

import { MetricsDatasourceResponses } from "@costory/types/endpoints/metricsDatasources"
import { VirtualDimensionsRequests } from "@costory/types/endpoints/virtualDimensions"

import { ALL_OTHERS } from "@costory/shared/const"

import { MetricsDatasourcesSelect } from "@costory/front/components/MetricsDatasourcesSelect"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { PreviewMetric } from "@costory/front/pages/VirtualDimensions/components/AutoSplit/PreviewMetric"
import { Summary } from "@costory/front/pages/VirtualDimensions/components/AutoSplit/Summary"
import { TableMappingManual } from "@costory/front/pages/VirtualDimensions/components/AutoSplit/TableMapping"
import { TableMappingIdentityRegex } from "@costory/front/pages/VirtualDimensions/components/AutoSplit/TableMappingIdentityRegex"
import {
  DocsStep1AutoSplit,
  DocsStep2AutoSplit,
} from "@costory/front/pages/VirtualDimensions/components/AutoSplit/docs"
import { validateRegex } from "@costory/front/pages/VirtualDimensions/components/AutoSplit/utils"
import { useMetricsDatasourcesListWithMetricsDefinitionQuery } from "@costory/front/queries/metricsDatasources"

interface AutoSplitTableProps {
  ruleCost: number
  availableDimensionValues: string[]
  values: Record<string, string>
  onRegexChange: (value: string) => void
  allocation: VirtualDimensionsRequests.EditVirtualDimension["rules"][number]["allocation"]
  setMappingType: (value: (typeof MAPPING_TYPES)[number]["value"]) => void
  onSelectDataSource: (datasourceId: string) => void
  onClose: () => void
  onMappingChange: ({
    metricName,
    vdimValue,
  }: {
    metricName: string
    vdimValue: string
  }) => void
  onVdimValueCreate: (value: string) => void
}

export const AutoSplitTable = (props: AutoSplitTableProps) => {
  const metricQuery = useMetricsDatasourcesListWithMetricsDefinitionQuery()

  return (
    <QueryWrapper query={metricQuery} allowEmptyArray>
      {({ data: metricsDatasources }) => (
        <_AutoSplitTable
          metricsDatasources={metricsDatasources.filter(
            (el) => el.metadata.metricsDefinition?.length,
          )}
          {...props}
        />
      )}
    </QueryWrapper>
  )
}

const MAPPING_TYPES = [
  { label: "Identity Mapping", value: "identity" },
  { label: "Regex Mapping", value: "regexMapping" },
  { label: "Manual Mapping", value: "mapping" },
]

const _AutoSplitTable = ({
  metricsDatasources,
  allocation,
  setMappingType,
  onSelectDataSource,
  values,
  onMappingChange,
  onVdimValueCreate,
  onRegexChange,
  onClose,
  availableDimensionValues,
}: AutoSplitTableProps & {
  metricsDatasources: MetricsDatasourceResponses.GetAllMetrics
}) => {
  const mappingType =
    "mappingType" in allocation ? allocation.mappingType : "identity"
  const [modalOpened, setModalOpened] = useState(false)
  const [selectedDatasource, setSelectedDatasource] = useState<string>(
    "datasource" in allocation ? allocation.datasource : "",
  )

  const selectedDatasourceMetrics = useMemo(() => {
    return (
      metricsDatasources.find((d) => d.id === selectedDatasource)?.metadata
        .metricsDefinition || []
    ).filter((el) => el.name !== ALL_OTHERS)
  }, [metricsDatasources, selectedDatasource])

  const handleDatasourceSelect = (datasourceId: string) => {
    setSelectedDatasource(datasourceId)
    onSelectDataSource(datasourceId)
  }
  const [regexValue, setRegexValue] = useState(
    "regexTransformation" in allocation ? allocation.regexTransformation : "",
  )
  console.log(regexValue, selectedDatasourceMetrics)

  return (
    <>
      <Summary
        mappingType={mappingType}
        datasource={
          metricsDatasources.find((d) => d.id === selectedDatasource)?.name
        }
        mapping={values}
        onClick={() => setModalOpened(true)}
        selectedDatasourceMetrics={selectedDatasourceMetrics}
        regexValue={
          "regexTransformation" in allocation
            ? allocation.regexTransformation
            : undefined
        }
      />

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Configure Dynamic Allocation based on Usage Metrics."
        size="80%"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <DocsStep1AutoSplit />
          <MetricsDatasourcesSelect
            data={metricsDatasources}
            value={selectedDatasource}
            onChange={(value) => handleDatasourceSelect(value ?? "")}
          />
          <PreviewMetric selectedDatasource={selectedDatasource} />

          <DocsStep2AutoSplit />
          <SegmentedControl
            data={MAPPING_TYPES}
            value={mappingType}
            onChange={setMappingType}
            disabled={!selectedDatasource}
          />
          <div style={{ height: 300 }}>
            <ScrollArea h={300}>
              {(mappingType === "identity" ||
                mappingType === "regexMapping") && (
                <TableMappingIdentityRegex
                  selectedDatasourceMetrics={selectedDatasourceMetrics}
                  mappingType={mappingType}
                  regexValue={regexValue || ""}
                  setRegexValue={setRegexValue}
                />
              )}

              {mappingType === "mapping" && (
                <TableMappingManual
                  selectedDataSource={selectedDatasourceMetrics}
                  availableDimensionValues={availableDimensionValues}
                  values={values}
                  onMappingChange={onMappingChange}
                  onVdimValueCreate={onVdimValueCreate}
                />
              )}
            </ScrollArea>
          </div>
        </Stack>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setModalOpened(false)}>
            Cancel
          </Button>
          <Tooltip
            label={
              mappingType === "mapping" &&
              Object.keys(values).filter((key) => values[key] !== "").length < 2
                ? "At least two mappings are required"
                : mappingType === "regexMapping" &&
                    validateRegex(regexValue) !== false
                  ? "Regex is not valid"
                  : "Save"
            }
          >
            <Button
              disabled={
                (mappingType === "mapping" &&
                  Object.keys(values).filter((key) => values[key] !== "")
                    .length < 2) ||
                (mappingType === "regexMapping" &&
                  validateRegex(regexValue) !== false)
              }
              onClick={() => {
                if (regexValue) onRegexChange(regexValue)
                setModalOpened(false)
                onClose()
              }}
            >
              Validate
            </Button>
          </Tooltip>
        </Group>
      </Modal>
    </>
  )
}
